// The "ticket" a finished login-page scan leaves behind so the portal can reopen the full
// report after sign-in. It holds the report access token, so it only lives as long as TTL_MS.
const KEY = 'vn-scan-report';
const TTL_MS = 24 * 60 * 60_000;
// A ticket this fresh opens the report by itself on the first portal page; older ones only
// show the sidebar card.
const AUTO_OPEN_MS = 30 * 60_000;

export interface ScanTicket {
  scanId: string;
  token: string;
  host: string;
  savedAt: number;
  /** Cleared once the popup has opened by itself, so a reload does not reopen it. */
  autoOpen: boolean;
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function readTicket(): ScanTicket | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Partial<ScanTicket>;
    if (!t.scanId || !t.token || !t.host || typeof t.savedAt !== 'number' || Date.now() - t.savedAt > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return {
      scanId: t.scanId,
      token: t.token,
      host: t.host,
      savedAt: t.savedAt,
      autoOpen: t.autoOpen === true,
    };
  } catch {
    return null;
  }
}

function write(ticket: ScanTicket | null) {
  try {
    if (ticket) localStorage.setItem(KEY, JSON.stringify(ticket));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable (private mode / quota): the report just is not carried over.
  }
  notify();
}

export const saveTicket = (scanId: string, token: string, host: string) =>
  write({ scanId, token, host, savedAt: Date.now(), autoOpen: true });

export const clearTicket = () => write(null);

/** True once, for a ticket fresh enough to open on its own; marks it as handled. */
export function takeAutoOpen(): boolean {
  const t = readTicket();
  if (!t?.autoOpen) return false;
  write({ ...t, autoOpen: false });
  return Date.now() - t.savedAt <= AUTO_OPEN_MS;
}

export function subscribeTicket(listener: () => void) {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}
